import { ReactNode, useEffect } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/auth/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { QUERY_KEYS } from '@/config/queries';
import { loadPendingInvitationClaim } from '@/lib/company-invite-utils';
import {
  buildInviteAuthRedirect,
  isAdministrationPath,
  requiresCompanyMembership,
  clearPendingGroupInvite,
  loadPendingGroupInvite,
  resolvePostAuthRedirect,
  savePendingGroupInvite,
} from '@/lib/auth-redirect';
import { resolveFirstRunRedirect } from '@/lib/first-run-routing';
import { createLogger } from '@/lib/structuredLogger';
import { PageLoadingSpinner } from '@/components/ui/loading-spinner';
import { AuthMfaChallenge } from '@/components/auth-mfa-challenge';
import { useWebAccess } from '@/providers/WebAccessProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const logger = createLogger('AuthGuard');

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

/**
 * Protected Route Component - Handles authentication-based navigation
 *
 * Shares admission with sensitive providers before resolving employee first-run state.
 */
export const ProtectedRoute = ({
  children,
  requireAuth = true,
  redirectTo = '/auth',
}: ProtectedRouteProps) => {
  const { user, loading, session } = useAuth();
  const { t } = useTranslation();
  const { mfaQuery, memberQuery, consentQuery, readinessGeneration } = useWebAccess();
  const location = useLocation();
  const queryClient = useQueryClient();
  const userId = user?.id ?? session?.user.id ?? '';
  const shouldCheckMfa = Boolean(requireAuth && userId);
  const mfaRequired = mfaQuery.data?.required === true;
  const needsMembership = requireAuth && requiresCompanyMembership(location.pathname, location.search);
  const memberReady = !needsMembership || (memberQuery.isSuccess && memberQuery.fetchStatus === 'idle' && memberQuery.data === true);
  const consentReady = !needsMembership || (consentQuery.isSuccess && consentQuery.fetchStatus === 'idle' && consentQuery.data?.consent_data_processing === true);
  const shouldResolveFirstRun = Boolean(
    requireAuth &&
    userId &&
    mfaQuery.isSuccess &&
    mfaQuery.fetchStatus === 'idle' &&
    !mfaRequired &&
    memberReady &&
    consentReady &&
    location.pathname !== '/health-consent' &&
    !isAdministrationPath(location.pathname),
  );
  const firstRunQuery = useQuery({
    queryKey: QUERY_KEYS.FIRST_RUN(userId, readinessGeneration),
    queryFn: () => resolveFirstRunRedirect(userId),
    enabled: shouldResolveFirstRun,
    staleTime: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    if (!location.pathname.startsWith('/join/')) return;
    // Consume only once the invite itself is allowed to render. Until then,
    // keep it through authentication, MFA, and first-run redirects.
    if (shouldResolveFirstRun && firstRunQuery.isSuccess && !firstRunQuery.data) {
      clearPendingGroupInvite();
    } else {
      savePendingGroupInvite(location.pathname);
    }
  }, [location.pathname, shouldResolveFirstRun, firstRunQuery.isSuccess, firstRunQuery.data]);

  // Show loading state while auth context is initializing
  if (loading) {
    return <PageLoadingSpinner />;
  }

  if (shouldCheckMfa && (mfaQuery.isPending || mfaQuery.fetchStatus !== 'idle')) {
    return <PageLoadingSpinner />;
  }

  if (shouldCheckMfa && (mfaQuery.isError || mfaRequired)) {
    return (
      <AuthMfaChallenge
        factors={mfaQuery.data?.verifiedFactors ?? []}
        loadFailed={mfaQuery.isError}
        onRetry={() => void mfaQuery.refetch()}
        onVerified={async () => {
          await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MFA_GATE(userId) });
          if (loadPendingInvitationClaim()) {
            window.location.href = buildInviteAuthRedirect(window.location.search);
          }
        }}
      />
    );
  }

  if (needsMembership && userId) {
    if (memberQuery.isPending || memberQuery.fetchStatus !== 'idle') return <PageLoadingSpinner />;
    if (!memberReady) return (
      <div className="flex min-h-dvh items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md">
          <CardHeader><CardTitle as="h1">{t(memberQuery.isError ? 'auth.companyAccessUnavailable' : 'auth.companyAccessRequired')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {!memberQuery.isError && <p className="text-body text-muted-foreground">{t('auth.contactAdmin')}</p>}
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void memberQuery.refetch()}>{t('common.retry')}</Button>
            </div>
            <Link className="inline-flex min-h-11 items-center text-primary underline underline-offset-4" to="/profile?section=privacy">{t('profile.tabs.privacy')}</Link>
          </CardContent>
        </Card>
      </div>
    );
    if (consentQuery.fetchStatus === 'paused') return (
      <div className="flex min-h-dvh items-center justify-center px-4 py-8" role="status">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle as="h1">{t('auth.accessPausedTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-body text-muted-foreground">{t('auth.accessPausedDescription')}</p>
            <Link
              className="inline-flex min-h-11 items-center text-primary underline underline-offset-4"
              to="/profile?section=privacy"
            >
              {t('profile.tabs.privacy')}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
    if (consentQuery.isPending || consentQuery.fetchStatus !== 'idle') return <PageLoadingSpinner />;
    if (consentQuery.isError) return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 py-8" role="alert">
        <p>{t('errors.profileLoadFailed')}</p>
        <Button onClick={() => void consentQuery.refetch()}>{t('common.retry')}</Button>
        <Link className="inline-flex min-h-11 items-center text-primary underline" to="/profile?section=privacy">{t('profile.tabs.privacy')}</Link>
      </div>
    );
    if (!consentReady) return <Navigate to="/health-consent" replace />;
  }

  if (shouldResolveFirstRun && firstRunQuery.isPending) {
    return <PageLoadingSpinner />;
  }

  if (shouldResolveFirstRun && firstRunQuery.isError) {
    return <Navigate to="/health-consent" replace />;
  }

  if (shouldResolveFirstRun && firstRunQuery.data && firstRunQuery.data !== location.pathname) {
    return <Navigate to={firstRunQuery.data} replace />;
  }

  if (
    shouldResolveFirstRun &&
    firstRunQuery.isSuccess &&
    !firstRunQuery.data &&
    location.pathname === '/dashboard'
  ) {
    const pendingInvite = loadPendingGroupInvite();
    if (pendingInvite) return <Navigate to={pendingInvite} replace />;
  }

  // If context has user or session and first-run state is valid, render children
  if (user || session) {
    return <>{children}</>;
  }

  // No user and no session - redirect to auth
  if (requireAuth) {
    return (
      <Navigate
        to={
          isAdministrationPath(location.pathname)
            ? `${redirectTo}?redirect=${encodeURIComponent(location.pathname)}`
            : redirectTo
        }
        state={{ from: location }}
        replace
      />
    );
  }

  return <>{children}</>;
};

/**
 * Auth State Change Handler - Handles navigation on auth events
 * * OPTIMIZED: Non-blocking navigation for instant perceived performance.
 */
export const AuthStateChangeHandler = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    logger.debug('Setting up auth state change handler');
    let authEventVersion = 0;

    const handleSignedIn = (eventVersion: number) => {
      if (eventVersion !== authEventVersion) return;
      const currentPath = window.location.pathname;
      savePendingGroupInvite(new URLSearchParams(window.location.search).get('redirect'));
      const postAuthRedirect = resolvePostAuthRedirect(window.location.search);
      const pendingInvitation = loadPendingInvitationClaim();
      const inviteCodeFromUrl =
        new URLSearchParams(window.location.search).get('invite')?.trim().toUpperCase() || '';
      const shouldCompleteInvite = Boolean(inviteCodeFromUrl || pendingInvitation?.code);

      // Auth owns its pending claim. Protected destinations own shared admission.
      if (currentPath === '/' || currentPath === '/app') {
        if (shouldCompleteInvite) {
          logger.info('Pending invitation claim detected, deferring redirect to auth screen');
          window.location.href = buildInviteAuthRedirect(window.location.search);
          return;
        }

        // The destination boundary owns membership, consent and first-run reads.
        window.location.href = postAuthRedirect;
        return;
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        authEventVersion += 1;
        clearPendingGroupInvite();
      }
      // Skip navigation for OAuth callback
      if (window.location.pathname.includes('/auth/fitbit/callback')) {
        return;
      }

      // Handle sign-in
      if (event === 'SIGNED_IN' && session) {
        const eventVersion = ++authEventVersion;
        window.setTimeout(() => {
          if (eventVersion !== authEventVersion) return;
          void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MFA_FACTORS(session.user.id) });
          handleSignedIn(eventVersion);
        }, 0);
      }
      // A factor can be enrolled or removed from another active device. Re-run
      // the fail-closed gate whenever Auth rotates or changes this session.
      else if (
        session &&
        (event === 'TOKEN_REFRESHED' ||
          event === 'USER_UPDATED' ||
          event === 'MFA_CHALLENGE_VERIFIED')
      ) {
        window.setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MFA_FACTORS(session.user.id) });
        }, 0);
      }
      // Handle sign-out
      else if (event === 'SIGNED_OUT') {
        queryClient.clear(); // Clear cache on logout
        logger.info('User signed out, redirecting to auth');
        navigate('/auth', { replace: true });
      }
    });

    return () => {
      authEventVersion += 1;
      subscription.unsubscribe();
    };
  }, [navigate, queryClient]);

  return null;
};
